import React, { useState, useCallback } from 'react';

import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled';

import { useChild } from '../../hooks/useChildren.tsx';
import type { A2UIComponentDefinition } from '../../types/A2UIComponents.types.tsx';
import type { A2UIModalProps } from '../../types/A2UIComponents.types.tsx';

type Props = {
	definition: A2UIComponentDefinition;
	visited?: Set<string>;
};

export const A2UIModal = ({ definition, visited }: Props): React.JSX.Element => {
	const def = definition as A2UIModalProps;
	const [isOpen, setIsOpen] = useState(false);

	const triggerContent = useChild(def.trigger, visited);
	const modalContent = useChild(def.content, visited);

	const label = def.accessibility?.label ?? 'Dialog';

	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);

	return (
		<React.Fragment>
			<Box
				onClick={handleOpen}
				role="button"
				tabIndex={0}
				onKeyDown={(e: { key: string }) => e.key === 'Enter' && handleOpen()}
			>
				{triggerContent}
			</Box>
			<ModalTransition>
				{isOpen && (
					// eslint-disable-next-line @atlaskit/design-system/no-modal-label
					<Modal onClose={handleClose} label={label}>
						<ModalHeader>
							<ModalTitle>{label}</ModalTitle>
						</ModalHeader>
						<ModalBody>{modalContent}</ModalBody>
					</Modal>
				)}
			</ModalTransition>
		</React.Fragment>
	);
};
